import { Plus, X } from 'lucide-react'
import { useEffect } from 'react'

import { useIsAdmin, useMe } from '@/api/endpoints/me'
import { Button } from '@/components/ui/Button'
import { Callout } from '@/components/ui/Callout'
import { IconButton } from '@/components/ui/IconButton'
import { Input, TimeInput } from '@/components/ui/Input'
import { Segmented } from '@/components/ui/Segmented'
import { SelectContent, SelectItem, SelectRoot, SelectTrigger, SelectValue } from '@/components/ui/Select'
import { Toggle } from '@/components/ui/Toggle'
import { FormErrorSummary } from '@/components/feedback/FormErrorSummary'
import { cn } from '@/lib/cn'
import {
  CORTES_FACTORY,
  CORTES_MAX_CLIPS,
  CORTES_MAX_SOURCES,
  LLM_OPTIONS,
  newSourceRow,
  NO_PRESET_VALUE,
  type ScheduleValues,
  type SourceRow,
} from './model'
import { Field, NumberInput, Section } from './parts'
import styles from './ChannelEditDialog.module.scss'

export interface GenerationTabProps {
  values: ScheduleValues
  errors: Record<string, string>
  set: <K extends keyof ScheduleValues>(key: K, value: ScheduleValues[K]) => void
  before: (key: keyof ScheduleValues) => string | undefined
  factoryChoices: ReadonlyArray<{ slug: string; label: string }>
  factoryChanged: boolean
  onUndoFactory: () => void
  presets: ReadonlyArray<{ id: string; name: string }>
  presetsPending: boolean
  sources: SourceRow[]
  sourcesLoading: boolean
  /** Fontes carregadas do servidor. Sem isso, editar não é seguro: o PUT substitui a lista inteira. */
  sourcesReady: boolean
  sourcesLoadError: boolean
  isYoutube: boolean
  sourcesError: string | null
  saveSourcesError: unknown
  onSourcesChange: (rows: SourceRow[]) => void
  newSourceKey: () => number
}

const SOURCE_FIELD_LABELS: Record<string, string> = {
  url: 'URL da fonte',
  alias: 'Apelido',
  max_clips: 'Máx. de cortes',
  enabled: 'Ativa',
  items: 'Fontes',
}

export function GenerationTab(props: GenerationTabProps) {
  const { values, errors, set, before, factoryChoices } = props
  const isCortes = values.factory === CORTES_FACTORY
  const factoryLabel = factoryChoices.find((f) => f.slug === values.factory)?.label ?? values.factory
  // O modelo local é só do admin (docs/plan-llm-local-admin.md): para os outros
  // a opção some, e um ``local`` já gravado volta a ``auto``, senão todo Salvar
  // mandaria o valor de volta e levaria 403. Só com o ``/me`` respondido: antes
  // disso o admin perderia o próprio ``local``.
  const isAdmin = useIsAdmin()
  const meKnown = useMe().isSuccess
  const llmRefused = meKnown && !isAdmin && values.llm_provider === 'local'
  useEffect(() => {
    if (llmRefused) set('llm_provider', 'auto')
  }, [llmRefused, set])
  const llmOptions = LLM_OPTIONS.filter((o) => isAdmin || o.value !== 'local')

  return (
    <div className={styles.tabPanel}>
      {props.isYoutube ? null : (
        <p className={cn(styles.hint, styles.mobileOnly)}>
          Posts de comunidade e interações só existem para canais do YouTube.
        </p>
      )}
      <Section title="Conteúdo">
        <div className={styles.grid2}>
          <Field
            id="ch-factory"
            label="Factory"
            help="Motor que gera os vídeos deste canal. Trocar de factory zera as prioridades de formato e o preset."
            before={before('factory')}
            error={errors.factory}
          >
            <SelectRoot
              value={values.factory}
              onValueChange={(v) => {
                set('factory', v)
              }}
            >
              <SelectTrigger id="ch-factory" aria-label="Factory">
                <SelectValue placeholder="Escolha" />
              </SelectTrigger>
              <SelectContent>
                {factoryChoices.map((f) => (
                  <SelectItem key={f.slug} value={f.slug}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          </Field>
          <Field
            id="ch-preset"
            label="Preset"
            help="Sem preset: herda do canal principal (mesma factory) ou usa os parâmetros já salvos. Com preset: os parâmetros salvos deste canal são substituídos."
            meta={values.preset_id === NO_PRESET_VALUE ? 'padrão do canal principal' : undefined}
            before={before('preset_id')}
          >
            <SelectRoot
              value={values.preset_id || NO_PRESET_VALUE}
              onValueChange={(v) => {
                set('preset_id', v)
              }}
            >
              <SelectTrigger id="ch-preset" aria-label="Preset" disabled={props.presetsPending}>
                <SelectValue placeholder={props.presetsPending ? 'Carregando…' : 'Sem preset'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_PRESET_VALUE}>Sem preset</SelectItem>
                {props.presets.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          </Field>
        </div>
        {props.factoryChanged ? (
          <Callout
            role="alert"
            title={`Trocar para ${factoryLabel} zera as prioridades e o preset`}
            action={
              <Button variant="secondary" size="sm" onClick={props.onUndoFactory}>
                Desfazer troca
              </Button>
            }
          >
            Ao salvar, a ordem dos formatos (aba Upload) e o preset deste canal voltam ao padrão.
            {isCortes ? null : ' As fontes de cortes deixam de aparecer.'}
          </Callout>
        ) : null}
      </Section>

      <Section title="Agenda">
        <div className={styles.grid2}>
          <Field id="ch-gen-time" label="Horário de geração" before={before('generation_time')} error={errors.generation_time}>
            <TimeInput
              id="ch-gen-time"
              value={values.generation_time}
              onChange={(v) => {
                set('generation_time', v)
              }}
              error={Boolean(errors.generation_time)}
            />
          </Field>
          <Field
            id="ch-vpd"
            label="Vídeos por dia"
            help="Quantos vídeos o ciclo de geração produz por dia."
            before={before('videos_per_day')}
            error={errors.videos_per_day}
            hint={isCortes ? 'Na Cortes, conta episódios processados, não cortes.' : undefined}
          >
            <NumberInput
              id="ch-vpd"
              min={0}
              max={10}
              suffix="0–10"
              value={values.videos_per_day}
              error={Boolean(errors.videos_per_day)}
              onChange={(v) => {
                set('videos_per_day', v ?? Number.NaN)
              }}
            />
          </Field>
        </div>
      </Section>

      <Section title="Modelo de IA">
        <Field
          label="Modelo de IA"
          help="Onde o roteiro e os metadados deste canal são gerados. Ranking, segurança e visão ficam sempre na nuvem."
          before={before('llm_provider')}
          hint="Vale só para roteiro e metadados (SEO)."
        >
          <Segmented
            aria-label="Modelo de IA"
            options={llmOptions.map((o) => ({ value: o.value, label: o.label }))}
            value={!isAdmin && values.llm_provider === 'local' ? 'auto' : values.llm_provider}
            onValueChange={(v) => {
              set('llm_provider', v)
            }}
          />
        </Field>
      </Section>

      {isCortes ? <CortesSources {...props} /> : null}
    </div>
  )
}

function CortesSources({
  sources,
  sourcesReady,
  sourcesLoading,
  sourcesLoadError,
  sourcesError,
  saveSourcesError,
  onSourcesChange,
  newSourceKey,
}: GenerationTabProps) {
  function update(key: number, patch: Partial<Omit<SourceRow, 'key'>>) {
    onSourcesChange(sources.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  }

  return (
    <Section title="Fontes de cortes" meta={`só factory Cortes · ${String(sources.length)}/${String(CORTES_MAX_SOURCES)}`}>
      {saveSourcesError ? <FormErrorSummary error={saveSourcesError} fieldLabels={SOURCE_FIELD_LABELS} /> : null}
      {sourcesLoadError && !sourcesReady ? (
        <p className={styles.hint}>Sem a lista atual, as fontes ficam só para leitura — o Salvar não as altera.</p>
      ) : null}
      {sourcesLoadError ? <p className={styles.error}>Não foi possível carregar as fontes deste canal.</p> : null}
      {sourcesLoading ? (
        <span className={styles.empty}>Carregando fontes…</span>
      ) : sources.length === 0 ? (
        <span className={styles.empty}>Nenhuma fonte — o Cortes não terá o que cortar neste canal.</span>
      ) : (
        <div className={styles.sourceList}>
          <div className={styles.sourceHead} aria-hidden>
            <span>Ativa</span>
            <span>Apelido</span>
            <span>URL</span>
            <span>Máx. cortes</span>
            <span />
          </div>
          {sources.map((row) => (
            <div key={row.key} className={cn(styles.sourceRow, !row.enabled && styles.sourcePaused)}>
              <Toggle
                aria-label={`Fonte ${row.alias || row.url} ${row.enabled ? 'ativa' : 'pausada'}`}
                checked={row.enabled}
                onCheckedChange={(v) => {
                  update(row.key, { enabled: v })
                }}
              />
              <Input
                aria-label="Apelido"
                placeholder="Apelido (ex.: flow)"
                maxLength={60}
                value={row.alias}
                onChange={(e) => {
                  update(row.key, { alias: e.target.value })
                }}
              />
              <Input
                aria-label="URL"
                placeholder="https://www.youtube.com/@canal/videos"
                value={row.url}
                onChange={(e) => {
                  update(row.key, { url: e.target.value })
                }}
              />
              <NumberInput
                id={`ch-source-${String(row.key)}-max`}
                aria-label="Máximo de cortes"
                min={1}
                max={CORTES_MAX_CLIPS}
                suffix="1–20"
                value={row.max_clips}
                onChange={(v) => {
                  update(row.key, { max_clips: v ?? Number.NaN })
                }}
              />
              <IconButton
                type="button"
                variant="ghost"
                aria-label={`Remover fonte ${row.alias || row.url}`}
                onClick={() => {
                  onSourcesChange(sources.filter((r) => r.key !== row.key))
                }}
              >
                <X />
              </IconButton>
            </div>
          ))}
        </div>
      )}
      <div>
        <Button
          variant="secondary"
          leadingIcon={<Plus />}
          disabled={!sourcesReady || sources.length >= CORTES_MAX_SOURCES}
          onClick={() => {
            onSourcesChange([...sources, newSourceRow(newSourceKey())])
          }}
        >
          Adicionar fonte
        </Button>
      </div>
      {sourcesError ? (
        <p className={styles.error} role="alert">
          {sourcesError}
        </p>
      ) : (
        <p className={styles.hint}>Fonte pausada fica salva, mas não gera cortes.</p>
      )}
    </Section>
  )
}
